export default `
# Return objects

These object types are used by Inbox service to represent shipment feedback returns and detailed state/proof/certificate/document information.

## FeedbackReturn

Summary returned by \`GET /api/inbox/feedback\`.

\`\`\`json
{
  "returnId": "<return identifier>",
  "returnType": "STATE",
  "description": "Shipment delivered",
  "shipmentCode": "PR5UKZ4NM",
  "ownerUser": "juan@acme.com",
  "recordedTime": "2026-07-01T12:00:00Z",
  "read": false
}
\`\`\`

| Field | Type | Description |
|-------|------|-------------|
| \`returnId\` | string | Internal identifier used to request detailed information or download files. |
| \`returnType\` | string | \`STATE\`, \`PROOF\`, \`CERT\`, \`DOCUMENT\`, \`STAMPED_COPY\` or \`REPORT\`. |
| \`description\` | string | Human-readable summary. |
| \`shipmentCode\` | string | Shipment code associated with the return. |
| \`ownerUser\` | string | Owner user email, when applicable. |
| \`recordedTime\` | datetime | Time when the return was stored in PostaMail. |
| \`read\` | boolean | Whether this return has already been marked as read. |

## StateInfo

Detailed state-change return.

| Field | Type | Description |
|-------|------|-------------|
| \`returnId\` | string | Feedback return identifier. |
| \`shipmentCode\` | string | Associated shipment code. |
| \`stateCode\` | string | Normalized state code, such as \`DEL\`, \`RET\`, \`WAY\` or \`CAN\`. |
| \`stateData\` | string | JSON string with carrier-specific key/value details. **Warning:** Fields and values may vary depending on the logistics operator. Use only as additional information supplementing the normalized \`stateCode\` field, not as a replacement for it. |
| \`stage\` | string | Processing stage, e.g. \`PREPARATION\`, \`DELIVERY\`, \`RETURN\`. |
| \`timestamp\` | datetime | Source timestamp for the state change. |

## ProofInfo

Detailed proof-of-delivery return.

| Field | Type | Description |
|-------|------|-------------|
| \`returnId\` | string | Feedback return identifier. |
| \`shipmentCode\` | string | Associated shipment code. |
| \`proofType\` | string | Proof type, such as \`PEE-DELIVERY-PDF\`, \`PEE-RETURN-XML\`, \`POD\`. |
| \`documentFileName\` | string | Proof document file name. |
| \`documentPages\` | integer | Number of pages, when applicable. |
| \`documentSize\` | integer | File size in bytes. |
| \`timestamp\` | datetime | Proof generation timestamp. |
| \`data\` | string | JSON string with additional proof data. |

## CertInfo

Detailed certificate return.

| Field | Type | Description |
|-------|------|-------------|
| \`returnId\` | string | Feedback return identifier. |
| \`shipmentCode\` | string | Associated shipment code. |
| \`certType\` | string | Certificate type. |
| \`documentFileName\` | string | Certificate file name. |
| \`documentPages\` | integer | Number of document pages. |
| \`documentSize\` | integer | File size in bytes. |
| \`timestamp\` | datetime | Certificate generation timestamp. |
| \`data\` | string | JSON string with additional certificate data. |

## DocumentInfo

Detailed generated-document return.

| Field | Type | Description |
|-------|------|-------------|
| \`returnId\` | string | Feedback return identifier. |
| \`shipmentCode\` | string | Associated shipment code. |
| \`documentFileName\` | string | Generated document file name. |
| \`documentPages\` | integer | Number of pages. |
| \`documentSize\` | integer | File size in bytes. |
| \`timestamp\` | datetime | Document generation timestamp. |
| \`data\` | string | JSON string with additional document data. |

## StampedCopyInfo

Detailed stamped-copy return. Stamped copies are available for Carta Administrativa products.

| Field | Type | Description |
|-------|------|-------------|
| \`returnId\` | string | Feedback return identifier. |
| \`shipmentCode\` | string | Associated shipment code. |
| \`stampedCopyType\` | string | Stamped-copy return type, typically \`STAMPED_COPY\`. |
| \`documentFileName\` | string | Stamped-copy file name. |
| \`documentPages\` | integer | Number of pages. |
| \`documentSize\` | integer | File size in bytes. |
| \`timestamp\` | datetime | Stamped-copy timestamp. |
| \`data\` | string | JSON string with additional stamped-copy data. |

`



// WEBPACK FOOTER //
// ./src/components/docs/api/content/returnObjects.js